import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Select } from "@/components/kit";
import { ScrollText, Filter, ShieldCheck } from "lucide-react";

const CAT_STYLE = {
  auth: "bg-olive/20 text-dark-olive border-olive/40",
  permission: "bg-maroon/15 text-maroon border-maroon/30",
  record: "bg-warm-cream text-ink border-ink/20",
  deletion: "bg-maroon/20 text-maroon border-maroon/40",
  financial: "bg-gold/25 text-ink border-gold/50",
  system: "bg-ink/10 text-ink/70 border-ink/20",
};

const PAGE = 50;

export default function AuditLog() {
  const [data, setData] = useState({ log: [], total: 0, facets: { modules: [], categories: [] } });
  const [filters, setFilters] = useState({ q: "", category: "", module: "" });
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE, skip };
      if (filters.q) params.q = filters.q;
      if (filters.category) params.category = filters.category;
      if (filters.module) params.module = filters.module;
      const r = await api.get("/audit-log", { params });
      setData(r.data);
    } finally { setLoading(false); }
  }, [filters, skip]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => { setSkip(0); setFilters({ ...filters, [k]: v }); };
  const fmt = (ts) => { try { return new Date(ts).toLocaleString(); } catch { return ts; } };

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-ink text-cream flex items-center justify-center"><ScrollText className="w-5 h-5" /></div>
        <h1 className="font-zilla text-3xl sm:text-4xl font-semibold">Audit Log</h1>
      </div>
      <p className="text-[17px] text-ink/70 mb-5 max-w-3xl">
        One immutable, system-wide record of who did what and when — permission changes, record edits,
        deletions, financial actions and more. Entries can never be edited or deleted, and are kept
        separately from Trash &amp; Reset Data. Every module writes here.
      </p>

      {/* Filters */}
      <div className="bg-cream border border-ink/15 rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-ink/50" />
        <input data-testid="audit-search" value={filters.q} onChange={(e) => setF("q", e.target.value)}
          placeholder="Search actor, action, target…"
          className="flex-1 min-w-[200px] bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
        <Select data-testid="audit-category" value={filters.category} onChange={(e) => setF("category", e.target.value)}
          className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]">
          <option value="">All categories</option>
          {data.facets.categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>
        <Select data-testid="audit-module" value={filters.module} onChange={(e) => setF("module", e.target.value)}
          className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]">
          <option value="">All modules</option>
          {data.facets.modules.map((m) => <option key={m} value={m}>{m}</option>)}
        </Select>
        <span className="text-[14px] text-ink/55 ml-auto" data-testid="audit-total">{data.total} entries</span>
      </div>

      {/* Entries */}
      <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden" data-testid="audit-list">
        {loading && data.log.length === 0 ? (
          <div className="p-10 text-center text-ink/55">Loading…</div>
        ) : data.log.length === 0 ? (
          <div className="p-12 text-center">
            <ShieldCheck className="w-10 h-10 text-ink/25 mx-auto mb-3" />
            <p className="text-ink/55 text-[16px]">No matching audit entries.</p>
          </div>
        ) : (
          data.log.map((e) => (
            <div key={e.id} className="px-5 py-3.5 border-b border-ink/8 last:border-0 flex items-start gap-4" data-testid={`audit-entry-${e.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${CAT_STYLE[e.category] || CAT_STYLE.system}`}>
                    {e.category || "system"}
                  </span>
                  {e.module && <span className="text-[12px] text-ink/50 uppercase tracking-wide">{e.module}</span>}
                  <span className="text-[15px] font-semibold">{e.detail || e.action}</span>
                </div>
                <div className="text-[13px] text-ink/60 mt-0.5">
                  <strong>{e.actor_name || e.actor_email}</strong>
                  {e.target_label && e.target_label !== "-" ? <> · target: {e.target_label}</> : null}
                  {e.target_email && e.target_email !== "-" && !e.target_label ? <> · {e.target_email}</> : null}
                  <span className="text-ink/40"> · {e.action}</span>
                </div>
              </div>
              <div className="text-[13px] text-ink/55 whitespace-nowrap shrink-0">{fmt(e.timestamp)}</div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {data.total > PAGE && (
        <div className="flex items-center justify-between mt-4">
          <button data-testid="audit-prev" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}
            className="border border-ink/20 rounded-lg px-4 py-2.5 min-h-[44px] text-[15px] font-semibold disabled:opacity-40">Previous</button>
          <span className="text-[14px] text-ink/55">{skip + 1}–{Math.min(skip + PAGE, data.total)} of {data.total}</span>
          <button data-testid="audit-next" disabled={skip + PAGE >= data.total} onClick={() => setSkip(skip + PAGE)}
            className="border border-ink/20 rounded-lg px-4 py-2.5 min-h-[44px] text-[15px] font-semibold disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}
